import { APP_TIME_ZONE } from '@saas/shared';

/**
 * Oficjalna wizualizacja PDF faktury KSeF — generator Ministerstwa Finansów
 * (`src/vendor/ksef-pdf-generator`, patrz README tam). Działa w przeglądarce: XML → PDF (Blob).
 *
 * Generator ładujemy leniwie (`import()`), żeby ~100 kB gzip + pdfmake z czcionkami nie trafiły
 * do głównego bundla — pobierają się dopiero przy pierwszym otwarciu podglądu.
 */

export interface KsefPdfInput {
  /** Pełny XML faktury (FA(2)/FA(3)/FA_RR) dokładnie tak, jak pobrany z KSeF. */
  xml: string;
  ksefNumber: string;
  /** Link weryfikacyjny KSeF (kod QR) — liczy go API; bez niego PDF powstaje bez QR. */
  verificationUrl?: string | null;
  /** Data przyjęcia w KSeF (ISO). */
  acquisitionDate?: string | null;
}

const WARSAW_DATE = new Intl.DateTimeFormat('pl-PL', {
  timeZone: APP_TIME_ZONE,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

/**
 * Znacznik czasu ISO → DD.MM.RRRR (format, którego oczekuje generator MF) w czasie polskim.
 * Data przyjęcia w KSeF to moment w czasie (API zwraca UTC) — faktura przyjęta tuż po północy
 * czasu polskiego ma mieć datę „dzisiejszą", nie wczorajszą z UTC. Sama data bez godziny
 * (YYYY-MM-DD) zostaje bez przeliczania strefy.
 */
export function formatKsefAcquisitionDate(iso: string | null | undefined): string | undefined {
  if (!iso) return undefined;
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (dateOnly) {
    const [, year, month, day] = dateOnly;
    return `${day}.${month}.${year}`;
  }
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return undefined;
  return WARSAW_DATE.format(parsed);
}

export async function generateKsefInvoicePdf(input: KsefPdfInput): Promise<Blob> {
  const { generateInvoice } = await import('@/vendor/ksef-pdf-generator/ksef-pdf-generator.min.js');
  const file = new File([input.xml], `${input.ksefNumber}.xml`, { type: 'application/xml' });
  return generateInvoice(
    file,
    {
      nrKSeF: input.ksefNumber,
      acDate: formatKsefAcquisitionDate(input.acquisitionDate),
      qrCode: input.verificationUrl ?? undefined,
    },
    'blob',
  );
}
